<template>
  <ol-vector-layer zIndex="6" :visible="isVisible">
    <ol-source-vector>
      <ol-feature v-for="(dasValue, index) in sensorReadings.slice(0, -1)" :key="index">
        <ol-geom-line-string
          :coordinates="getAcousticCoordinates(index, true)"
        ></ol-geom-line-string>
        <ol-style>
          <ol-style-stroke
            line-cap="butt"
            line-join="miter"
            :color="strokeColor"
            :width="strokeWidth"
          ></ol-style-stroke>
        </ol-style>
      </ol-feature>
    </ol-source-vector>
  </ol-vector-layer>
  <ol-vector-layer :visible="isVisible">
    <ol-source-vector>
      <ol-feature v-for="(_, index) in sensorReadings.slice(0, -1)" :key="index">
        <ol-geom-line-string
          :coordinates="getAcousticCoordinates(index, false)"
        ></ol-geom-line-string>
        <ol-style>
          <ol-style-stroke
            line-cap="butt"
            line-join="miter"
            :color="strokeColor"
            :width="strokeWidth"
          ></ol-style-stroke>
        </ol-style>
      </ol-feature>
    </ol-source-vector>
  </ol-vector-layer>
</template>

<script setup lang="ts">
import { MapCableDataPoint } from '@/server/MakeSenseApp-backend'
import { ref, defineProps, PropType } from 'vue'

const strokeWidth = ref(4)
const strokeColor = ref('#06A3DA')

const acousticLenghtConstant = ref(150)

const props = defineProps({
  sensorReadings: {
    type: Array as PropType<MapCableDataPoint[]>,
    required: true
  },
  isVisible: {
    type: Boolean,
    required: true
  }
})

const getSlope = (x1: number, y1: number, x2: number, y2: number) => {
  return (y2 - y1) / (x2 - x1)
}

const getPerpendicularSlope = (a: number) => {
  return -1 / a
}

// Vector [1,a]
const getSlopeUnitVector = (a: number) => {
  const magnitude = Math.sqrt(1 + a * a)
  return [1 / magnitude, a / magnitude]
}

const getAcousticCoordinates = (i: number, direction: boolean) => {
  const das = props.sensorReadings[i].das ?? 0

  const [x1, y1] = [
    props.sensorReadings[i].coordinate?.easting ?? 0,
    props.sensorReadings[i].coordinate?.northing ?? 0
  ]
  const [x2, y2] = [
    props.sensorReadings[i + 1].coordinate?.easting ?? 0,
    props.sensorReadings[i + 1].coordinate?.northing ?? 0
  ]

  const slope = getSlope(x1, y1, x2, y2)

  const slope_perpendicular = getPerpendicularSlope(slope)

  const [unitX, unitY] = getSlopeUnitVector(slope_perpendicular)

  const scaledX = unitX * (das * acousticLenghtConstant.value + 300)
  const scaledY = unitY * (das * acousticLenghtConstant.value + 300)

  if (direction) {
    return [
      [x1, y1],
      [x1 + scaledX, y1 + scaledY]
    ]
  } else {
    return [
      [x1, y1],
      [x1 - scaledX, y1 - scaledY]
    ]
  }
}
</script>

<style scoped></style>
